'use client';

/**
 * ChatSurface — registry-driven chat peer in the right rail.
 *
 * Renders the cards declared in the matrix's `chat_surface_cards` for
 * the active (mode, selection), stacked 1×N inside the surface body.
 * Mounted as a peer rail surface alongside Insights / Copilot /
 * Composer — visibility is a CSS toggle the parent (`RailColumn`)
 * applies based on `useRailSurfaceStore.active === 'chat'`. Cards
 * are looked up via `CHAT_CARD_REGISTRY` and read shared state
 * (single fetch, sibling tab, compose context) from the
 * `<ChatSurfaceProvider />` mounted here.
 *
 * No dialog chrome — the surface is a peer, not an overlay. No
 * header bar, no close button, no Escape handler. The rail icon
 * strip handles swap/close; the parent positions us via
 * `absolute inset-0` and CSS-hides us when another peer is active.
 *
 * The legacy `<ChatDrawer />` continues to render as an overlay for
 * (mode, selection) entries whose matrix leaves `chat_surface_cards`
 * empty — per-mode migration. `desktop-shell.tsx::RailColumn` picks
 * which path based on `useRegistryChat`.
 *
 * @see klyde_edge/ui_config/total_matrix.py — chat_surface_cards
 * @see klyde_edge/composition/cards/messaging.py
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @component_role: Card Surface
 * @component_type: Layout Peer
 * @domain: Frontend Shell / Messaging
 * @layer: presentation
 */

import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { ChatSurfaceProvider } from './chat-surface-context';
import { CHAT_CARD_REGISTRY } from './chat-card-registry';

export function ChatSurface() {
  const { mode, contentSelection: selection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);

  const cfg = getViewportConfig(mode, selection);
  const cardKeys = cfg?.chat_surface_cards ?? [];

  return (
    <div className="flex flex-col h-full bg-background border-l border-border">
      <ChatSurfaceProvider>
        {cardKeys.map((key, i) => {
          const Component = CHAT_CARD_REGISTRY[key];
          if (!Component) {
            if (typeof window !== 'undefined') {
              // eslint-disable-next-line no-console
              console.warn(
                `[ChatSurface] Unknown card key '${key}' — add to CHAT_CARD_REGISTRY`,
              );
            }
            return null;
          }
          // First card claims remaining vertical space (with its own
          // internal scroll); subsequent cards sit at natural height
          // below it (chat_thread on top, compose_message anchored
          // beneath it). Without this split the second card renders
          // below the body's overflow-hidden cutline and disappears.
          const wrapperClass =
            i === 0
              ? 'flex-1 min-h-0 overflow-hidden'
              : 'flex-shrink-0 border-t border-border/40';
          return (
            <div key={key} className={wrapperClass}>
              <Component />
            </div>
          );
        })}
      </ChatSurfaceProvider>
    </div>
  );
}
